import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DSBackLink, DSIcon, DSInput, DSTextarea } from '../design-system/primitives';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import './MaintenanceTemplateEditor.css';

// Screen 4 of the Machine Maintenance feature (docs/MACHINE_MAINTENANCE_REQUIREMENTS.md
// §5, design reference: MaintenanceScreens.tsx TemplateEditor). Organizer/Admin
// only - one global Checklist Template applies across all machines.

const EMPTY_FORM = { title: '', instructions: '', rationale: '', mediaUrl: '' };

function MaintenanceTemplateEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getChecklistItems({ includeInactive: true });
      setItems(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load the checklist template.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const activeItems = useMemo(
    () => items.filter((i) => i.is_active).sort((a, b) => a.sort_order - b.sort_order),
    [items]
  );
  const inactiveItems = useMemo(() => items.filter((i) => !i.is_active), [items]);

  const handleMove = async (item, direction) => {
    const idx = activeItems.findIndex((i) => i.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= activeItems.length) return;
    const other = activeItems[swapIdx];

    try {
      await Promise.all([
        apiService.updateChecklistItem(item.id, { sortOrder: other.sort_order }),
        apiService.updateChecklistItem(other.id, { sortOrder: item.sort_order })
      ]);
      loadItems();
    } catch (err) {
      showToast('Failed to reorder items.', 'error');
    }
  };

  const handleDeactivate = async (item) => {
    try {
      await apiService.deactivateChecklistItem(item.id);
      showToast(`"${item.title}" deactivated.`, 'success');
      loadItems();
    } catch (err) {
      showToast('Failed to deactivate item.', 'error');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await apiService.reactivateChecklistItem(item.id);
      showToast(`"${item.title}" restored.`, 'success');
      loadItems();
    } catch (err) {
      showToast('Failed to restore item.', 'error');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setExpandedId(null);
    setEditForm({
      title: item.title || '',
      instructions: item.instructions || '',
      rationale: item.rationale || '',
      mediaUrl: item.media_url || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) {
      showToast('Title is required.', 'error');
      return;
    }
    setSaving(true);
    try {
      await apiService.updateChecklistItem(editingId, editForm);
      showToast('Item saved.', 'success');
      setEditingId(null);
      loadItems();
    } catch (err) {
      showToast('Failed to save item.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.title.trim()) {
      showToast('Title is required.', 'error');
      return;
    }
    setSaving(true);
    try {
      await apiService.createChecklistItem({ ...newItem, sortOrder: activeItems.length + 1 });
      showToast('Item added.', 'success');
      setNewItem(EMPTY_FORM);
      setShowAddForm(false);
      loadItems();
    } catch (err) {
      showToast('Failed to add item.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="maintenance-template">
      <div className="mt-header">
        <DSBackLink to="/machines/maintenance">Back to Maintenance</DSBackLink>
        <div className="mt-header__top">
          <div>
            <h1 className="mt-header__title">Checklist Template</h1>
            <p className="mt-header__subtitle">{activeItems.length} active items · applied to all machines</p>
          </div>
          <button type="button" className="mt-add-btn" onClick={() => setShowAddForm(true)}>
            + Add
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!loading && (
        <>
          {showAddForm && (
            <div className="mt-form mt-form--new">
              <p className="mt-form__heading">New Item</p>
              <label className="mt-field-label">Title</label>
              <DSInput
                type="text"
                className="mt-input"
                value={newItem.title}
                onChange={(e) => setNewItem((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Wax Playfield"
              />
              <label className="mt-field-label">Instructions</label>
              <DSTextarea
                className="mt-textarea"
                value={newItem.instructions}
                onChange={(e) => setNewItem((p) => ({ ...p, instructions: e.target.value }))}
                placeholder="Step-by-step instructions…"
              />
              <label className="mt-field-label">Rationale</label>
              <DSTextarea
                className="mt-textarea"
                value={newItem.rationale}
                onChange={(e) => setNewItem((p) => ({ ...p, rationale: e.target.value }))}
                placeholder="Why this step matters…"
              />
              <label className="mt-field-label">Media URL (optional)</label>
              <DSInput
                type="text"
                className="mt-input"
                value={newItem.mediaUrl}
                onChange={(e) => setNewItem((p) => ({ ...p, mediaUrl: e.target.value }))}
                placeholder="Photo or video URL"
              />
              <div className="mt-form__actions">
                <button type="button" className="mt-btn-primary" onClick={handleAddItem} disabled={saving || !newItem.title.trim()}>
                  Add Item
                </button>
                <button type="button" className="mt-btn-secondary" onClick={() => { setShowAddForm(false); setNewItem(EMPTY_FORM); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <p className="mt-section-heading">Active Items</p>
          <div className="mt-item-list">
            {activeItems.map((item, idx) => {
              const isEditing = editingId === item.id;
              const isExpanded = expandedId === item.id && !isEditing;

              return (
                <div key={item.id} className="mt-item">
                  {isEditing ? (
                    <div className="mt-form">
                      <p className="mt-form__heading">Edit Item</p>
                      <label className="mt-field-label">Title</label>
                      <DSInput
                        type="text"
                        className="mt-input"
                        value={editForm.title}
                        onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                      />
                      <label className="mt-field-label">Instructions</label>
                      <DSTextarea
                        className="mt-textarea"
                        value={editForm.instructions}
                        onChange={(e) => setEditForm((p) => ({ ...p, instructions: e.target.value }))}
                      />
                      <label className="mt-field-label">Rationale</label>
                      <DSTextarea
                        className="mt-textarea"
                        value={editForm.rationale}
                        onChange={(e) => setEditForm((p) => ({ ...p, rationale: e.target.value }))}
                      />
                      <label className="mt-field-label">Media URL (optional)</label>
                      <DSInput
                        type="text"
                        className="mt-input"
                        value={editForm.mediaUrl}
                        onChange={(e) => setEditForm((p) => ({ ...p, mediaUrl: e.target.value }))}
                      />
                      <div className="mt-form__actions">
                        <button type="button" className="mt-btn-primary" onClick={handleSaveEdit} disabled={saving}>
                          Save
                        </button>
                        <button type="button" className="mt-btn-secondary" onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mt-item__row">
                        <div className="mt-item__reorder">
                          <button type="button" onClick={() => handleMove(item, 'up')} disabled={idx === 0}><DSIcon name="chevronUp" /></button>
                          <button type="button" onClick={() => handleMove(item, 'down')} disabled={idx === activeItems.length - 1}><DSIcon name="chevronDown" /></button>
                        </div>
                        <span className="mt-item__order">{item.sort_order}</span>
                        <button type="button" className="mt-item__title-btn" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                          {item.title}
                        </button>
                        <div className="mt-item__actions">
                          <button type="button" className="mt-item__icon-btn" onClick={() => startEdit(item)} title="Edit"><DSIcon name="edit" /></button>
                          <button type="button" className="mt-item__icon-btn mt-item__icon-btn--danger" onClick={() => handleDeactivate(item)} title="Deactivate"><DSIcon name="x" /></button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="mt-item__preview">
                          {item.instructions && <p className="mt-item__preview-text">{item.instructions}</p>}
                          {item.rationale && <p className="mt-item__preview-rationale">{item.rationale}</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {inactiveItems.length > 0 && (
            <>
              <p className="mt-section-heading">Deactivated</p>
              <div className="mt-inactive-list">
                {inactiveItems.map((item) => (
                  <div key={item.id} className="mt-inactive-row">
                    <p className="mt-inactive-row__title">{item.title}</p>
                    <button type="button" className="mt-inactive-row__restore" onClick={() => handleReactivate(item)}>
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default MaintenanceTemplateEditor;
